import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyDashboard, updateMyBeneficiaries } from '../../services/members';
import { useAuth } from '../../hooks/useAuth';
import Toast from '../../components/Common/Toast';
import { Heart, Plus, Trash2, ArrowLeft } from 'lucide-react';

// Relationship options
const RELATIONSHIP_OPTIONS = [
  { value: 'child', label: 'Child' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
];

const Beneficiaries = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [member, setMember] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadMember();
  }, []);

  const loadMember = async () => {
    try {
      const data = await getMyDashboard();
      console.log('Dashboard data:', data);
      setMember(data.member);

      const beneficiaryList = data.member?.beneficiaries || 
                            data.member?.Beneficiaries || 
                            data.beneficiaries || 
                            [];
      
      const loaded = beneficiaryList.map(b => ({
        id: b.id,
        full_name: b.full_name,
        relationship: b.relationship || '',
      }));
      setBeneficiaries(loaded);
    } catch (err) {
      console.error('Failed to load beneficiaries:', err);
      setToast({ type: 'error', message: 'Failed to load beneficiaries' });
    } finally {
      setLoading(false);
    }
  };

  const addBeneficiary = () => {
    if (beneficiaries.length >= 10) {
      setToast({ type: 'error', message: 'Maximum 10 beneficiaries allowed' });
      return;
    }
    setBeneficiaries([
      ...beneficiaries,
      { full_name: '', relationship: '', tempId: Date.now() },
    ]);
  };

  const updateBeneficiary = (index, field, value) => {
    const updated = [...beneficiaries];
    updated[index][field] = value;
    setBeneficiaries(updated);
  };

  const removeBeneficiary = (index) => {
    const updated = beneficiaries.filter((_, i) => i !== index);
    setBeneficiaries(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate all fields are filled
    const empty = beneficiaries.some(b => !b.full_name.trim() || !b.relationship.trim());
    if (empty) {
      setToast({ type: 'error', message: 'Please fill in all beneficiary details' });
      return;
    }
    setSaving(true);
    try {
      const payload = beneficiaries.map(b => ({
        full_name: b.full_name,
        relationship: b.relationship,
        allocation: 0,
      }));
      await updateMyBeneficiaries(payload);
      setToast({ type: 'success', message: 'Beneficiaries updated successfully' });
      setTimeout(() => navigate('/dashboard/member'), 1500);
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Update failed' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] px-4">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent"></div>
      <p className="text-slate-500 mt-4 text-sm">Loading beneficiaries...</p>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900">Beneficiaries</h1>
          <p className="text-sm text-slate-500">Manage your dependents ({beneficiaries.length}/10)</p>
        </div>
        <button
          onClick={() => navigate('/dashboard/member')}
          className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 transition-colors text-sm font-medium"
        >
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Beneficiary list – scrollable */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="max-h-[400px] overflow-y-auto p-4 space-y-4">
            {beneficiaries.length === 0 ? (
              <div className="text-center py-8">
                <Heart className="mx-auto text-slate-300 mb-3" size={48} />
                <p className="text-slate-500">No beneficiaries added yet.</p>
                <p className="text-xs text-slate-400 mt-1">Add up to 10 dependents.</p>
              </div>
            ) : (
              beneficiaries.map((ben, idx) => (
                <div key={ben.tempId || ben.id || idx} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Full Name"
                      value={ben.full_name || ''}
                      onChange={(e) => updateBeneficiary(idx, 'full_name', e.target.value)}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm"
                      required
                    />
                    <div className="flex gap-2">
                      <select
                        value={ben.relationship || ''}
                        onChange={(e) => updateBeneficiary(idx, 'relationship', e.target.value)}
                        className="flex-1 px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm appearance-none"
                        required
                      >
                        <option value="">Select relationship...</option>
                        {RELATIONSHIP_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeBeneficiary(idx)}
                        className="p-2.5 text-rose-500 hover:bg-rose-50 rounded-xl transition-colors shrink-0"
                        aria-label="Remove beneficiary"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Add button */}
        <button
          type="button"
          onClick={addBeneficiary}
          disabled={beneficiaries.length >= 10}
          className={`flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl font-semibold text-sm transition-colors ${
            beneficiaries.length >= 10
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
          }`}
        >
          <Plus size={18} />
          {beneficiaries.length >= 10 ? 'Maximum reached' : 'Add Beneficiary'}
        </button>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-200">
          <button
            type="submit"
            disabled={saving || beneficiaries.length === 0}
            className="flex-1 bg-indigo-600 text-white py-3.5 rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50 transition-colors min-h-[48px]"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/dashboard/member')}
            className="flex-1 sm:flex-none px-6 py-3.5 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-colors min-h-[48px]"
          >
            Cancel
          </button>
        </div>
      </form>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default Beneficiaries;